import { useState } from "react";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";

type Comment = {
  id: string;
  category: string;
  number_value: string | null;
  comment_text: string;
  is_active: boolean;
  created_at: string;
};

const CATEGORIES = [
  {
    key: "destiny",
    label: "Kader Sayısı",
    values: [
      "1","2","3","4","5","6","7","8","9",
      "10/1","19/1","28/1","37/1","46/1",
      "20/2","29/2","38/2","47/2",
      "12/3","21/3","30/3","39/3","48/3",
      "13/4","22/4","31/4","40/4","49/4",
      "14/5","23/5","32/5","41/5",
      "15/6","24/6","33/6","42/6","51/6",
      "16/7","25/7","34/7","43/7",
      "17/8","26/8","35/8","44/8",
      "18/9","27/9","36/9","45/9",
    ],
  },
  { key: "heart_desire",  label: "Kalp Arzusu",    values: ["1","2","3","4","5","6","7","8","9"] },
  { key: "expression",   label: "İfade Sayısı",    values: ["1","2","3","4","5","6","7","8","9"] },
  { key: "birthday",     label: "Doğum Günü",      values: ["1","2","3","4","5","6","7","8","9"] },
  { key: "personality",  label: "Kişilik Sayısı",  values: ["1","2","3","4","5","6","7","8","9"] },
  { key: "karmic_debt",  label: "Karmik Borç",     values: ["1","2","3","4","5","6","7","8","9"] },
  { key: "karmic_clean", label: "Karmik Temiz",    values: [] },
];

async function fetchComments(): Promise<Comment[]> {
  const res = await adminFetch("/api/admin/onboarding-comments");
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? "Yorumlar alınamadı.");
  return Array.isArray(json?.data) ? json.data : [];
}

async function createComment(payload: Omit<Comment, "id" | "created_at">): Promise<Comment> {
  const res = await adminFetch("/api/admin/onboarding-comments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? "Yorum eklenemedi.");
  return json.data;
}

async function updateComment(id: string, payload: Partial<Comment>): Promise<Comment> {
  const res = await adminFetch(`/api/admin/onboarding-comments/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? "Yorum güncellenemedi.");
  return json.data;
}

async function deleteComment(id: string): Promise<void> {
  const res = await adminFetch(`/api/admin/onboarding-comments/${encodeURIComponent(id)}`, { method: "DELETE" });
  const json = await res.json().catch(() => null) as any;
  if (!res.ok) throw new Error(json?.error ?? "Yorum silinemedi.");
}

export default function AdminNumerolojiOnboardingYorumlar() {
  const queryClient = useQueryClient();
  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const [selectedCat, setSelectedCat] = useState(CATEGORIES[0].key);
  const [selectedNum, setSelectedNum] = useState<string | null>(null);
  const [formText, setFormText] = useState("");
  const [formActive, setFormActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  const listQuery = useQuery({ queryKey: ["onboarding-comments"], queryFn: fetchComments });

  const createMut = useMutation({
    mutationFn: createComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-comments"] });
      setFormText("");
      setFormActive(true);
      setSaveError(null);
    },
    onError: (e: Error) => setSaveError(e.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<Comment> }) => updateComment(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-comments"] });
      setEditingId(null);
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteComment,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["onboarding-comments"] }),
  });

  if (sessionQuery.isLoading) return <div className="p-8 text-sm text-muted-foreground">Yükleniyor...</div>;

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  const cat = CATEGORIES.find((c) => c.key === selectedCat)!;
  const allComments = listQuery.data ?? [];
  const catComments = allComments.filter(
    (c) => c.category === selectedCat && (selectedNum === null ? true : c.number_value === selectedNum),
  );

  function handleSave() {
    if (!formText.trim()) return;
    setSaveError(null);
    createMut.mutate({
      category: selectedCat,
      number_value: cat.values.length === 0 ? null : selectedNum,
      comment_text: formText.trim(),
      is_active: formActive,
    });
  }

  function startEdit(c: Comment) {
    setEditingId(c.id);
    setEditText(c.comment_text);
    setEditActive(c.is_active);
  }

  function confirmEdit(c: Comment) {
    updateMut.mutate({ id: c.id, payload: { ...c, comment_text: editText, is_active: editActive } });
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        {/* Header */}
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />
              <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100">Onboarding Yorumları</h1>
            </div>
            <button
              onClick={() => logoutMutation.mutate()}
              className="rounded-lg px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Çıkış
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 gap-0">
          {/* Kategori paneli */}
          <aside className="w-52 shrink-0 border-r border-slate-200/70 bg-white/50 p-3 dark:border-slate-800/60 dark:bg-slate-950/20">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Kategori</p>
            <nav className="grid gap-1">
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => { setSelectedCat(c.key); setSelectedNum(null); setFormText(""); setSaveError(null); }}
                  className={`rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
                    selectedCat === c.key
                      ? "bg-blue-600/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-300"
                      : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                  }`}
                >
                  <span>{c.label}</span>
                  <span className="ml-1.5 text-[11px] text-slate-400">
                    ({allComments.filter((x) => x.category === c.key).length})
                  </span>
                </button>
              ))}
            </nav>
          </aside>

          {/* Sağ panel */}
          <main className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
            {/* Sayı seçimi */}
            {cat.values.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Sayı</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedNum(null)}
                    className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${
                      selectedNum === null
                        ? "border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                        : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                    }`}
                  >
                    Tümü
                  </button>
                  {cat.values.map((v) => (
                    <button
                      key={v}
                      onClick={() => setSelectedNum(v)}
                      className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${
                        selectedNum === v
                          ? "border-blue-500 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300"
                          : "border-slate-200 bg-white text-slate-500 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Yeni yorum formu */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-4 dark:border-slate-700/60 dark:bg-slate-900/60">
              <p className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
                Yeni Yorum Ekle
                {selectedNum && <span className="ml-1.5 text-blue-500">— {selectedNum}</span>}
              </p>

              <p className="mb-1.5 text-[11px] text-slate-400">
                Kullanıcı adını eklemek için <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">[isim]</code> yazın.
              </p>

              <textarea
                value={formText}
                onChange={(e) => setFormText(e.target.value)}
                placeholder="Yorum metni..."
                rows={4}
                className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />

              <div className="mt-2 flex items-center gap-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    className="h-4 w-4 rounded"
                  />
                  Aktif
                </label>
                <button
                  onClick={handleSave}
                  disabled={createMut.isPending || !formText.trim()}
                  className="ml-auto flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {createMut.isPending ? "Kaydediliyor..." : "Ekle"}
                </button>
              </div>
              {saveError && <p className="mt-2 text-xs text-red-500">{saveError}</p>}
            </div>

            {/* Kayıtlı yorumlar */}
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                Kayıtlı Yorumlar ({catComments.length})
              </p>

              {listQuery.isLoading && <p className="text-sm text-slate-400">Yükleniyor...</p>}

              {catComments.length === 0 && !listQuery.isLoading && (
                <p className="text-sm text-slate-400">Henüz yorum yok.</p>
              )}

              <div className="grid gap-3">
                {catComments.map((c) => (
                  <div
                    key={c.id}
                    className="rounded-xl border border-slate-200/80 bg-white p-4 dark:border-slate-700/60 dark:bg-slate-900/60"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      {c.number_value && (
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                          {c.number_value}
                        </span>
                      )}
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          c.is_active
                            ? "bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-slate-100 text-slate-400 dark:bg-slate-800"
                        }`}
                      >
                        {c.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </div>

                    {editingId === c.id ? (
                      <div>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={4}
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:border-blue-400 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                        />
                        <div className="mt-2 flex items-center gap-3">
                          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <input
                              type="checkbox"
                              checked={editActive}
                              onChange={(e) => setEditActive(e.target.checked)}
                              className="h-4 w-4 rounded"
                            />
                            Aktif
                          </label>
                          <div className="ml-auto flex gap-2">
                            <button
                              onClick={() => setEditingId(null)}
                              className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                            >
                              <X className="h-3 w-3" /> İptal
                            </button>
                            <button
                              onClick={() => confirmEdit(c)}
                              disabled={updateMut.isPending}
                              className="flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              <Check className="h-3 w-3" /> Kaydet
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">
                          {c.comment_text.length > 200 ? c.comment_text.slice(0, 200) + "…" : c.comment_text}
                        </p>
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => startEdit(c)}
                            className="flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                          >
                            <Pencil className="h-3 w-3" /> Düzenle
                          </button>
                          <button
                            onClick={() => {
                              if (!confirm("Bu yorumu silmek istiyor musunuz?")) return;
                              deleteMut.mutate(c.id);
                            }}
                            className="flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs text-red-500 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-3 w-3" /> Sil
                          </button>
                          <span className="ml-auto text-[11px] text-slate-300 dark:text-slate-600">
                            {new Date(c.created_at).toLocaleDateString("tr-TR")}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
